import ModalCustom from '../utils/ModalCustom/ModalCustom';
import { Button, Card, Checkbox, Input, message, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n/i18n';
import './ChangeDeviceLocationFromInstallation.scss';
import { PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { Device } from '../../gen/proto/api/gateway/v1/device_pb';
import { deviceApi } from '../../api/device';
import { useParams } from 'react-router-dom';
import { DeviceTrackerType } from '../../gen/proto/api/gateway/v1/device_tracker_pb';
import useAuthStore from '../../stores/auth.store';

const { Text } = Typography;
const PAGE_INDEX = 1;
const PAGE_SIZE = 20;
const MIN_SEARCH_LENGTH = 3;

interface ChangeDeviceLocationFromInstallationProps {
  onSuccess?: () => void;
}

const ChangeDeviceLocationFromInstallation = ({ onSuccess }: ChangeDeviceLocationFromInstallationProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { installationUuid } = useParams();
  const { t } = useTranslation('installation-location', {
    keyPrefix: 'installationLocationDetail.installation.summary.addDeviceModal',
  });
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<Device[]>([]);
  const [existingDeviceUuids, setExistingDeviceUuids] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);

  const handleSearch = async () => {
    if (searchValue.length < MIN_SEARCH_LENGTH) {
      setDevices([]);
      return;
    }
    const devices = await deviceApi.getDevicesByName(searchValue, PAGE_INDEX, PAGE_SIZE);
    setDevices(devices ?? []);
  };

  useEffect(() => {
    if (!isModalOpen || !installationUuid) return;
    let active = true;
    deviceApi
      .getDeviceByInstallation(installationUuid)
      .then((installationDevices) => {
        if (active) setExistingDeviceUuids(installationDevices.map((d) => d.uuid));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [isModalOpen, installationUuid]);

  const updateDeviceLocation = async () => {
    if (!installationUuid || selectedDevices.length === 0) return;
    try {
      setIsSubmitting(true);
      await Promise.all(
        selectedDevices.map((device) =>
          deviceApi.newDeviceLocation(device.uuid, installationUuid, DeviceTrackerType.INSTALLATION),
        ),
      );
      setIsModalOpen(false);
      setSelectedDevices([]);
      setSearchValue('');
      onSuccess?.();
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDevice = (device: Device, checked: boolean) => {
    setSelectedDevices((prev) => (checked ? [...prev, device] : prev.filter((d) => d.uuid !== device.uuid)));
  };

  return (
    <>
      {contextHolder}
      <Button onClick={() => setIsModalOpen(true)} type={'primary'} className={'AddDeviceModal__button'}>
        <PlusOutlined />
        {t('addDevice')}
      </Button>
      <ModalCustom
        title={t('addDevice')}
        description={t('description')}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        footer={[
          <Button
            key={'add'}
            type={'primary'}
            onClick={updateDeviceLocation}
            loading={isSubmitting}
            disabled={selectedDevices.length === 0}
          >
            {t('add')}
          </Button>,
        ]}
      >
        <div className={'AddDeviceModal'}>
          {isSuperAdmin && (
            <Card className={'AddDeviceModal__Card'}>
              <div className={'AddDeviceModal__warningCard'}>
                <WarningOutlined />
                <Text>{t('warning')}</Text>
              </div>
            </Card>
          )}
          <Input
            placeholder={t('search')}
            value={searchValue}
            onChange={(e) => {
              const value = e.target.value;
              setSearchValue(value);
              if (value.length < MIN_SEARCH_LENGTH) {
                setDevices([]);
              }
            }}
            onPressEnter={handleSearch}
          />

          <div className={'AddDeviceModal__Devices'}>
            {devices.map((device) => {
              const alreadyAdded = existingDeviceUuids.includes(device.uuid);
              return (
                <Checkbox
                  key={device.uuid}
                  checked={alreadyAdded || selectedDevices.some((d) => d.uuid === device.uuid)}
                  disabled={alreadyAdded}
                  onChange={(e) => toggleDevice(device, e.target.checked)}
                >
                  {device.name}
                </Checkbox>
              );
            })}
          </div>
        </div>
      </ModalCustom>
    </>
  );
};

export default ChangeDeviceLocationFromInstallation;
